// =============================================
// DEUDAS HISTÓRICAS - CON FILTRO DE AÑO
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Grid,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const DebtList = () => {
  const [debts, setDebts] = useState([]);
  const [users, setUsers] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [searchYearsBack, setSearchYearsBack] = useState(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Diálogo de nueva deuda
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '',
    debt_type: 'mensualidad',
    description: '',
    amount: '',
    month: '',
    year: new Date().getFullYear(),
    debt_date: new Date().toISOString().split('T')[0],
  });

  // Diálogo de aplicar pago
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [paymentData, setPaymentData] = useState({
    user_id: '',
    amount: '',
  });
  const [paymentResult, setPaymentResult] = useState(null);

  // Diálogo de editar deuda
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingDebt, setEditingDebt] = useState(null);

  // Diálogo de confirmar eliminación
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deletingDebt, setDeletingDebt] = useState(null);

  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Ordenamiento
  const [orderBy, setOrderBy] = useState('debt_date');
  const [order, setOrder] = useState('desc');

  // Búsqueda
  const [searchText, setSearchText] = useState('');

  // Filtros
  const [filters, setFilters] = useState({
    user_id: '',
    status: '',
    debt_type: '',
    year: '',
  });

  // Estadísticas
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
  });

  // Cargar datos iniciales
  useEffect(() => {
    const initData = async () => {
      await loadUsers();
      await loadAvailableYears();
      await loadDebts();
    };
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recargar cuando cambien los filtros (opcional, si quieres búsqueda automática)
  // useEffect(() => {
  //   loadDebts();
  // }, [filters]);

  // Cargar usuarios
  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      console.log('Usuarios cargados:', response.data);
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
      setError('Error al cargar lista de propietarios');
    }
  };
  const loadAvailableYears = async () => {
    try {
      // Obtener el parámetro search_years_back desde la BD
      const paramsResponse = await api.get('/system-parameters');
      const params = paramsResponse.data.parameters || [];
      const searchYearsParam = params.find(p => p.param_key === 'search_years_back');

      let yearsBack = 3; // Default
      if (searchYearsParam) {
        yearsBack = parseInt(searchYearsParam.param_value) || 3;
        setSearchYearsBack(yearsBack);
      }

      // Generar años dinámicamente
      const currentYear = new Date().getFullYear();
      const years = Array.from({ length: yearsBack }, (_, i) => currentYear - i);
      setAvailableYears(years);

      console.log(`📅 Años generados: ${years.join(', ')} (${yearsBack} años hacia atrás)`);
    } catch (err) {
      console.error('❌ Error al cargar años:', err);
      // Fallback: usar default de 3 años
      const currentYear = new Date().getFullYear();
      const defaultYears = Array.from({ length: 3 }, (_, i) => currentYear - i);
      setAvailableYears(defaultYears);
      console.log('⚠️ Error en API, usando fallback de 3 años');
    }
  };

  // Cargar deudas con filtros
  const loadDebts = async () => {
    try {
      setLoading(true);
      setError('');

      // Construir query params
      const params = new URLSearchParams();
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.debt_type) params.append('debt_type', filters.debt_type);
      if (filters.year) params.append('year', filters.year);

      const queryString = params.toString();
      const url = queryString ? `/debts?${queryString}` : '/debts';

      console.log('Cargando deudas con URL:', url);
      const response = await api.get(url);
      console.log('Deudas cargadas:', response.data);

      const debtList = response.data.debts || [];
      setDebts(debtList);

      // Calcular estadísticas
      const totalAmount = debtList.reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
      const pendingAmount = debtList
        .filter(d => d.status === 'pendiente')
        .reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);

      setStats({
        total: totalAmount,
        pending: pendingAmount,
      });

    } catch (err) {
      console.error('Error al cargar deudas:', err);
      setError('Error al cargar deudas: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros
  const handleFilter = () => {
    loadDebts();
  };

  // Limpiar filtros
  const handleClearFilters = () => {
    setFilters({
      user_id: '',
      status: '',
      debt_type: '',
      year: '',
    });
    // Recargar sin filtros
    setTimeout(() => {
      loadDebts();
    }, 100);
  };

  // Abrir diálogo
  const handleOpenDialog = () => {
    setFormData({
      user_id: '',
      debt_type: 'mensualidad',
      description: '',
      amount: '',
      month: '',
      year: new Date().getFullYear(),
      debt_date: new Date().toISOString().split('T')[0],
    });
    setOpenDialog(true);
  };

  // Cerrar diálogo
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      user_id: '',
      debt_type: 'mensualidad',
      description: '',
      amount: '',
      month: '',
      year: new Date().getFullYear(),
      debt_date: new Date().toISOString().split('T')[0],
    });
  };

  // Crear nueva deuda
  const handleCreateDebt = async () => {
    try {
      // Validaciones
      if (!formData.user_id || !formData.description || !formData.amount || !formData.debt_date) {
        setError('Por favor completa todos los campos requeridos');
        return;
      }

      setLoading(true);
      setError('');

      await api.post('/debts', formData);

      setSuccess('Deuda registrada exitosamente');
      handleCloseDialog();
      loadDebts();

    } catch (err) {
      setError('Error al crear deuda: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Abrir diálogo de pago
  const handleOpenPaymentDialog = () => {
    setPaymentData({
      user_id: '',
      amount: '',
    });
    setPaymentResult(null);
    setOpenPaymentDialog(true);
  };

  // Cerrar diálogo de pago
  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    setPaymentData({
      user_id: '',
      amount: '',
    });
    setPaymentResult(null);
  };

  // Aplicar pago
  const handleApplyPayment = async () => {
    try {
      // Validaciones
      if (!paymentData.user_id || !paymentData.amount || parseFloat(paymentData.amount) <= 0) {
        setError('Por favor selecciona un propietario e ingresa un monto válido');
        return;
      }

      setLoading(true);
      setError('');

      const response = await api.post('/debts/apply-payment', paymentData);

      setPaymentResult(response.data);
      setSuccess(`Pago aplicado: $${response.data.total_applied.toFixed(2)} a ${response.data.applied_debts.length} deuda(s)`);
      loadDebts();

    } catch (err) {
      setError('Error al aplicar pago: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Abrir diálogo de editar
  const handleOpenEditDialog = (debt) => {
    setEditingDebt({
      id: debt.id,
      description: debt.description,
      amount: debt.amount,
      month: debt.month || '',
      year: debt.year || new Date().getFullYear(),
      debt_date: debt.debt_date.split('T')[0],
      status: debt.status,
    });
    setOpenEditDialog(true);
  };

  // Cerrar diálogo de editar
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingDebt(null);
  };

  // Actualizar deuda
  const handleUpdateDebt = async () => {
    try {
      if (!editingDebt.description || !editingDebt.amount || !editingDebt.debt_date) {
        setError('Por favor completa todos los campos requeridos');
        return;
      }

      setLoading(true);
      setError('');

      await api.put(`/debts/${editingDebt.id}`, editingDebt);

      setSuccess('Deuda actualizada exitosamente');
      handleCloseEditDialog();
      loadDebts();

    } catch (err) {
      setError('Error al actualizar deuda: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Abrir diálogo de eliminar
  const handleOpenDeleteDialog = (debt) => {
    setDeletingDebt(debt);
    setOpenDeleteDialog(true);
  };

  // Cerrar diálogo de eliminar
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeletingDebt(null);
  };

  // Eliminar deuda
  const handleDeleteDebt = async () => {
    try {
      setLoading(true);
      setError('');

      await api.delete(`/debts/${deletingDebt.id}`);

      setSuccess('Deuda eliminada exitosamente');
      handleCloseDeleteDialog();
      loadDebts();

    } catch (err) {
      setError('Error al eliminar deuda: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambio de página
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Manejar cambio de filas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Manejar ordenamiento
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Función de comparación para ordenamiento
  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  // Filtrar y ordenar deudas
  const filteredDebts = debts
    .filter((debt) => {
      if (!searchText) return true;
      const search = searchText.toLowerCase();
      return (
        debt.user_name?.toLowerCase().includes(search) ||
        debt.description?.toLowerCase().includes(search) ||
        debt.house_number?.toString().includes(search) ||
        debt.amount?.toString().includes(search)
      );
    })
    .sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      if (orderBy === 'house_number') {
        const aNum = parseInt(aVal);
        const bNum = parseInt(bVal);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          aVal = aNum;
          bVal = bNum;
        }
      }

      if (order === 'desc') {
        return bVal < aVal ? -1 : 1;
      } else {
        return aVal < bVal ? -1 : 1;
      }
    });

  // Deudas paginadas
  const paginatedDebts = filteredDebts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-PA');
  };

  // Formatear monto
  const formatAmount = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  // Obtener color del estado
  const getStatusColor = (status) => {
    const colors = {
      pendiente: 'error',
      pagada: 'success',
      cancelada: 'default',
    };
    return colors[status] || 'default';
  };

  // Obtener color del tipo
  const getTypeColor = (type) => {
    const colors = {
      mensualidad: 'primary',
      extraordinaria: 'warning',
      otro: 'info',
    };
    return colors[type] || 'default';
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Deudas Históricas
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PaymentIcon />}
            color="info"
            onClick={handleOpenPaymentDialog}
          >
            Aplicar Pago
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Nueva Deuda
          </Button>
        </Box>
      </Box>

      {/* Mensajes */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterIcon sx={{ mr: 1 }} />
          Filtros
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Búsqueda global */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              id="search-global"
              label="Buscar"
              placeholder="Nombre, descripción, casa..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Propietario */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              sx={{ width: 260 }}
              id="filter-propietario"
              label="Propietario"
              value={filters.user_id}
              onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
            >
              <MenuItem value="">Todos</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name} - Casa {user.house_number}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Tipo */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              sx={{ width: 100 }}
              id="filter-tipo"
              label="Tipo"
              value={filters.debt_type}
              onChange={(e) => setFilters({ ...filters, debt_type: e.target.value })}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="mensualidad">Mensualidad</MenuItem>
              <MenuItem value="extraordinaria">Extraordinaria</MenuItem>
              <MenuItem value="otro">Otro</MenuItem>
            </TextField>
          </Grid>

          {/* Estado */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              sx={{ width: 100 }}
              id="filter-estado"
              label="Estado"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="" sx={{ width: 80 }}>Todos</MenuItem>
              <MenuItem value="pendiente">Pendiente</MenuItem>
              <MenuItem value="pagada">Pagada</MenuItem>
              <MenuItem value="cancelada">Cancelada</MenuItem>
            </TextField>
          </Grid>

          {/* Año - NUEVO */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              sx={{ width: 80 }}
              id="filter-año"
              label="Año"
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: e.target.value })}
            >
              <MenuItem value="">Todos</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Botones */}
          <Grid item xs={12} sm={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleFilter}
              >
                Buscar
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleClearFilters}
              >
                Limpiar
              </Button>
              <IconButton color="primary" onClick={loadDebts}>
                <RefreshIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        {/* Badges de estadísticas */}
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Chip
            label={`${filteredDebts.length} deuda${filteredDebts.length !== 1 ? 's' : ''}`}
            color="primary"
          />
          <Chip
            label={`Total: ${formatAmount(stats.total)}`}
            color="info"
          />
          <Chip
            label={`Pendiente: ${formatAmount(stats.pending)}`}
            color="error"
          />
          {searchText && (
            <Chip
              label={`Búsqueda: "${searchText}"`}
              onDelete={() => setSearchText('')}
              color="secondary"
              variant="outlined"
            />
          )}
        </Box>
      </Paper>

      {/* Tabla */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'user_name'}
                    direction={orderBy === 'user_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('user_name')}
                  >
                    <strong>Propietario</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'house_number'}
                    direction={orderBy === 'house_number' ? order : 'asc'}
                    onClick={() => handleRequestSort('house_number')}
                  >
                    <strong>Casa</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'debt_type'}
                    direction={orderBy === 'debt_type' ? order : 'asc'}
                    onClick={() => handleRequestSort('debt_type')}
                  >
                    <strong>Tipo</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell><strong>Descripción</strong></TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'original_amount'}
                    direction={orderBy === 'original_amount' ? order : 'asc'}
                    onClick={() => handleRequestSort('original_amount')}
                  >
                    <strong>Monto Original</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'amount'}
                    direction={orderBy === 'amount' ? order : 'asc'}
                    onClick={() => handleRequestSort('amount')}
                  >
                    <strong>Saldo</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'year'}
                    direction={orderBy === 'year' ? order : 'asc'}
                    onClick={() => handleRequestSort('year')}
                  >
                    <strong>Período</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'debt_date'}
                    direction={orderBy === 'debt_date' ? order : 'asc'}
                    onClick={() => handleRequestSort('debt_date')}
                  >
                    <strong>Fecha</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    <strong>Estado</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center"><strong>Acciones</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredDebts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography color="text.secondary">
                      {searchText || filters.user_id || filters.status || filters.debt_type || filters.year
                        ? 'No se encontraron deudas con los filtros aplicados'
                        : 'No hay deudas registradas'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedDebts.map((debt) => (
                  <TableRow key={debt.id} hover>
                    <TableCell>
                      {debt.user_name}
                    </TableCell>
                    <TableCell>
                      <Chip label={`#${debt.house_number}`} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={debt.debt_type}
                        size="small"
                        color={getTypeColor(debt.debt_type)}
                      />
                    </TableCell>
                    <TableCell>{debt.description}</TableCell>
                    <TableCell align="right">
                      {formatAmount(debt.original_amount)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        fontWeight="bold"
                        color={parseFloat(debt.amount) > 0 ? 'error' : 'success.main'}
                      >
                        {formatAmount(debt.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {debt.month && debt.year ? (
                        <Chip
                          label={`${debt.month}/${debt.year}`}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {formatDate(debt.debt_date)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={debt.status}
                        size="small"
                        color={getStatusColor(debt.status)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(debt)}
                          title="Editar"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(debt)}
                          title="Eliminar"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginación */}
        <TablePagination
          component="div"
          count={filteredDebts.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      {/* Diálogo Nueva Deuda */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Registrar Nueva Deuda</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Propietario */}
            <TextField
              select
              required
              fullWidth
              label="Propietario"
              value={formData.user_id}
              onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
            >
              <MenuItem value="">Seleccionar...</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name} - Casa {user.house_number}
                </MenuItem>
              ))}
            </TextField>

            {/* Tipo */}
            <TextField
              select
              required
              fullWidth
              label="Tipo de Deuda"
              value={formData.debt_type}
              onChange={(e) => setFormData({ ...formData, debt_type: e.target.value })}
            >
              <MenuItem value="mensualidad">Mensualidad</MenuItem>
              <MenuItem value="extraordinaria">Extraordinaria</MenuItem>
              <MenuItem value="otro">Otro</MenuItem>
            </TextField>

            {/* Descripción */}
            <TextField
              required
              fullWidth
              label="Descripción"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Ej: Cuota mensual Enero 2025"
            />

            {/* Monto */}
            <TextField
              required
              fullWidth
              type="number"
              label="Monto"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
              }}
            />

            <Grid container spacing={2}>
              {/* Mes (solo para mensualidad) */}
              {formData.debt_type === 'mensualidad' && (
                <Grid item xs={6}>
                  <TextField
                    select
                    fullWidth
                    label="Mes"
                    value={formData.month}
                    onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                  >
                    <MenuItem value="">Sin especificar</MenuItem>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {/* Año */}
              <Grid item xs={formData.debt_type === 'mensualidad' ? 6 : 12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Año"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                />
              </Grid>
            </Grid>

            {/* Fecha */}
            <TextField
              required
              fullWidth
              type="date"
              label="Fecha de la Deuda"
              value={formData.debt_date}
              onChange={(e) => setFormData({ ...formData, debt_date: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleCreateDebt}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Registrar Deuda'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo Aplicar Pago */}
      <Dialog open={openPaymentDialog} onClose={handleClosePaymentDialog} maxWidth="md" fullWidth>
        <DialogTitle>Aplicar Pago a Deudas</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>

            {!paymentResult ? (
              <>
                {/* Formulario de pago */}
                <Alert severity="info">
                  El pago se aplicará automáticamente a las deudas más antiguas del propietario seleccionado.
                </Alert>

                {/* Propietario */}
                <TextField
                  select
                  required
                  fullWidth
                  label="Propietario"
                  value={paymentData.user_id}
                  onChange={(e) => setPaymentData({ ...paymentData, user_id: e.target.value })}
                >
                  <MenuItem value="">Seleccionar...</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name} - Casa {user.house_number}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Monto */}
                <TextField
                  required
                  fullWidth
                  type="number"
                  label="Monto del Pago"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  }}
                  helperText="Ingrese el monto total que el propietario está pagando"
                />
              </>
            ) : (
              <>
                {/* Resultado del pago */}
                <Alert severity="success">
                  <Typography variant="h6" gutterBottom>
                    ¡Pago Aplicado Exitosamente!
                  </Typography>
                  <Typography>
                    Monto aplicado: <strong>${paymentResult.total_applied.toFixed(2)}</strong>
                  </Typography>
                  {paymentResult.remaining_amount > 0 && (
                    <Typography color="warning.main">
                      Sobrante: ${paymentResult.remaining_amount.toFixed(2)}
                    </Typography>
                  )}
                </Alert>

                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Deudas Afectadas ({paymentResult.applied_debts.length})
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Descripción</TableCell>
                        <TableCell align="right">Monto Aplicado</TableCell>
                        <TableCell align="right">Saldo Restante</TableCell>
                        <TableCell align="center">Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentResult.applied_debts.map((debt, index) => (
                        <TableRow key={index}>
                          <TableCell>{debt.description}</TableCell>
                          <TableCell align="right">
                            ${debt.amount_applied.toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            ${debt.remaining.toFixed(2)}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={debt.status}
                              size="small"
                              color={debt.status === 'pagada' ? 'success' : 'warning'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </>
            )}

          </Box>
        </DialogContent>
        <DialogActions>
          {!paymentResult ? (
            <>
              <Button onClick={handleClosePaymentDialog}>Cancelar</Button>
              <Button
                onClick={handleApplyPayment}
                variant="contained"
                disabled={loading}
                color="primary"
              >
                {loading ? 'Aplicando...' : 'Aplicar Pago'}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleClosePaymentDialog}
              variant="contained"
            >
              Cerrar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Diálogo Editar Deuda */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Deuda</DialogTitle>
        <DialogContent>
          {editingDebt && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

              {/* Descripción */}
              <TextField
                required
                fullWidth
                label="Descripción"
                value={editingDebt.description}
                onChange={(e) => setEditingDebt({ ...editingDebt, description: e.target.value })}
              />

              {/* Monto */}
              <TextField
                required
                fullWidth
                type="number"
                label="Monto"
                value={editingDebt.amount}
                onChange={(e) => setEditingDebt({ ...editingDebt, amount: e.target.value })}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
              />

              <Grid container spacing={2}>
                {/* Mes */}
                <Grid item xs={6}>
                  <TextField
                    select
                    fullWidth
                    label="Mes"
                    value={editingDebt.month}
                    onChange={(e) => setEditingDebt({ ...editingDebt, month: e.target.value })}
                  >
                    <MenuItem value="">Sin especificar</MenuItem>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Año */}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Año"
                    value={editingDebt.year}
                    onChange={(e) => setEditingDebt({ ...editingDebt, year: e.target.value })}
                  />
                </Grid>
              </Grid>

              {/* Fecha */}
              <TextField
                required
                fullWidth
                type="date"
                label="Fecha de la Deuda"
                value={editingDebt.debt_date}
                onChange={(e) => setEditingDebt({ ...editingDebt, debt_date: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
              />

              {/* Estado */}
              <TextField
                select
                fullWidth
                label="Estado"
                value={editingDebt.status}
                onChange={(e) => setEditingDebt({ ...editingDebt, status: e.target.value })}
              >
                <MenuItem value="pendiente">Pendiente</MenuItem>
                <MenuItem value="pagada">Pagada</MenuItem>
                <MenuItem value="cancelada">Cancelada</MenuItem>
              </TextField>

            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancelar</Button>
          <Button
            onClick={handleUpdateDebt}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Actualizar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo Confirmar Eliminación */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          {deletingDebt && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                ¿Estás seguro de que deseas eliminar esta deuda?
              </Alert>

              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Descripción:</strong>
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {deletingDebt.description}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  <strong>Monto:</strong>
                </Typography>
                <Typography variant="h6" color="error">
                  ${parseFloat(deletingDebt.amount).toFixed(2)}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Propietario:</strong>
                </Typography>
                <Typography variant="body1">
                  {deletingDebt.user_name} - Casa {deletingDebt.house_number}
                </Typography>
              </Paper>

              <Alert severity="error" sx={{ mt: 2 }}>
                Esta acción no se puede deshacer.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
          <Button
            onClick={handleDeleteDebt}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container >
  );
};

export default DebtList;
